import { useEffect, useRef, useState, type FC, type FormEvent } from "react";
import { PageOptions } from "../../types/page";
import { useTranslation } from "../../contexts/useTranslation";
import Navbar from "../../components/Navbar";
import ArrowLeftIcon from "../../components/icons/ArrowLeftIcon";
import ArrowRightIcon from "../../components/icons/ArrowRightIcon";
import storageService from "../../core/services/storageService";
import { useCore } from "../../contexts/useCore";
import { useWidget } from "../../contexts/useWidget";
import { isValidEmail } from "../../App";
import { Slot, Templated } from "../../templating";

interface LoginPageProps {
  navigate: (pageOptions: PageOptions) => void;
  isFullscreen: boolean;
}

const LoginPage: FC<LoginPageProps> = ({ navigate, isFullscreen }) => {
  const { translate } = useTranslation();
  const core = useCore();
  const { storageScope } = useWidget();
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const emailInputRef = useRef<HTMLInputElement>(null);

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const trimmedLoginEmail = loginEmail.trim();
    // Guard against programmatic / Enter-key submits that bypass the disabled
    // submit button — without this, an invalid address would still hit the
    // backend and route the user to errorPage on the inevitable 422.
    if (!isValidEmail(trimmedLoginEmail)) return;
    try {
      // core.loginWithEmail() handles storageService.setContact + state.contact internally.
      await core.loginWithEmail(trimmedLoginEmail);
      navigate({ page: { type: "enterCode", params: { email: trimmedLoginEmail } } });
    } catch (err) {
      const status = (err as { cause?: { status?: number } })?.cause?.status;
      if (status !== 499) {
        navigate({ page: { type: "errorPage" } });
      }
    }
  };

  useEffect(() => {
    setSubmitEnabled(isValidEmail(loginEmail));
  }, [loginEmail]);

  return (
    <Templated id="lastbot-login-page">
      <>
        <Slot name="navbar">
          <Navbar isFullscreen={isFullscreen} hideLogin>
            <button
              type="button"
              onClick={() => navigate({ page: storageService.getPageBeforeLogin(storageScope) })}
              className="-ml-1 text-lastbot-icon-muted cursor-pointer"
            >
              <ArrowLeftIcon />
            </button>
          </Navbar>
        </Slot>
        <div className="flex-1 flex flex-col gap-y-3 px-4 text-lastbot-on-surface justify-center items-center text-center max-w-[490px] w-full self-center">
          <div className="text-3xl tracking-wide text-lastbot-on-surface">
            <Slot name="form-title">{translate("login_with_one_time_code")}</Slot>
          </div>
          <div className="text-xs/5 text-lastbot-icon-muted sm:px-2">
            <Slot name="form-description">{translate("enter_email_to_receive_a_one_time_code")}</Slot>
          </div>
          <div className="w-full mt-2 sm:px-4">
            <form
              onSubmit={handleFormSubmit}
              className="bg-lastbot-surface-muted rounded-full relative"
            >
              <Slot name="email-input">
                <input
                  type="email"
                  ref={emailInputRef}
                  value={loginEmail}
                  onChange={(event) => setLoginEmail(event.target.value)}
                  placeholder={translate("enter_your_email")}
                  className="block w-full bg-transparent text-lastbot-on-surface resize-none h-[54px] py-3 pl-6 pr-14 placeholder:text-lastbot-on-surface-subtle focus:outline-hidden"
                  autoFocus
                  data-testid={`login-email-input`}
                />
              </Slot>
              <button
                type="submit"
                disabled={!submitEnabled}
                className="absolute bottom-0 right-[7px] bg-lastbot-send-surface text-lastbot-on-send-surface rounded-full p-2 mb-[7.5px] cursor-pointer disabled:text-lastbot-disabled-on-surface disabled:cursor-not-allowed disabled:bg-lastbot-disabled-surface"
                data-testid={`login-submit`}
              >
                <Slot name="submit-button"><ArrowRightIcon /></Slot>
              </button>
            </form>
          </div>
        </div>
      </>
    </Templated>
  );
};

export default LoginPage;
